import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Card,
  CardBody,
  Spinner,
  Alert,
  Table,
} from "reactstrap";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow"
import StopIcon from "@mui/icons-material/Stop"; 
import ComponentCard from "./ComponentCard";
import {
  startUserVmAPI,
  listUserVmsAPI,
  startUserVmInstanceAPI,
  stopUserVmInstanceAPI,
} from "../../service/ManageUserVMsService";
import {
  getOrganizationsAPI, 
  getUsersByOrgAPI
} from "../../service/ManageUserCreditsService";
import "../../App.css";

const authService = require("../../service/AuthService");

const trim = (s) => (s ?? "").trim();
const pad2 = (n) => String(n).padStart(2, "0");

function formatUtc(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt;
  const mm = pad2(d.getUTCMonth() + 1);
  const dd = pad2(d.getUTCDate());
  const yyyy = d.getUTCFullYear();
  const hh = pad2(d.getUTCHours());
  const min = pad2(d.getUTCMinutes());
  return `${mm}-${dd}-${yyyy} ${hh}:${min} UTC`;
}

export default function ManageUserVMs() {
  const [orgOptions, setOrgOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersCache, setUsersCache] = useState({}); // { [org]: ["user1","user2"] }
  const sessionUser = authService.getUser?.() || {};
  const defaultOrg = (sessionUser?.organization || "").trim().toLowerCase();
  const username = (sessionUser.user_name || sessionUser.username || sessionUser.name || "").toLowerCase().trim();

  // --- FORM INPUTS ---
  const [targetOrg, setTargetOrg] = useState(defaultOrg);
  const [targetUser, setTargetUser] = useState("");
  const [instanceName, setInstanceName] = useState("");
  const [acceptCosts, setAcceptCosts] = useState(false);

  const [acceptCostsErr, setAcceptCostsErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", msg: "" });

  // --- VM STATUS (records from tc_user_vms) ---
  const [vmRows, setVmRows] = useState([]);
  const [statusError, setStatusError] = useState("");
  const [statusLoading, setStatusLoading] = useState(false);

  const validateAcceptCosts = (val) => {
    if (!val)
      return "You must acknowledge that this will consume credits / incur cloud costs.";
    return "";
  };

  const formValid = targetOrg && targetUser && instanceName && acceptCosts;

  useEffect(() => {
    (async () => {
      try {
        setOrgLoading(true);
        const data = await getOrganizationsAPI({ limit: 500 });
        const items = Array.isArray(data?.items) ? data.items : [];
        setOrgOptions(items);
      } catch (e) {
        setStatus({ type: "danger", msg: e?.message || "Failed to load organizations." });
      } finally {
        setOrgLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const org = trim(targetOrg).toLowerCase();
    if (!org) {
      setUserOptions([]);
      setTargetUser("");
      return;
    }

    // cached?
    if (usersCache[org]) {
      const cached = usersCache[org];
      setUserOptions(cached);
      // ensure selected user is valid
      if (targetUser && !cached.includes(targetUser)) setTargetUser("");
      return;
    }

    (async () => {
      try {
        setUsersLoading(true);
        const data = await getUsersByOrgAPI(org, { limit: 2000 });
        const items = Array.isArray(data?.items) ? data.items : [];
        setUserOptions(items);
        setUsersCache((prev) => ({ ...prev, [org]: items }));
        if (targetUser && !items.includes(targetUser)) setTargetUser("");
      } catch (e) {
        setUserOptions([]);
        setTargetUser("");
        setStatus({ type: "danger", msg: e?.message || "Failed to load users for org." });
      } finally {
        setUsersLoading(false);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetOrg]);


  /* --------------------- Submit: Launch VM --------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: "", msg: "" });
    setAcceptCostsErr("");

    if (!targetOrg || !targetUser || !instanceName) {
      setStatus({
        type: "danger",
        msg: "Organization, username, and instance name are required.",
      });
      return;
    }

    const costErr = validateAcceptCosts(acceptCosts);
    if (costErr) {
      setAcceptCostsErr(costErr);
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        action: "launchUserVm",
        organization: targetOrg.trim().toLowerCase(),
        requestedBy: username,
        instanceName: instanceName.trim(), // backend uses this for EC2 Name + tc_user_vms.instance_name
      };

      const resp = await startUserVmAPI(payload);

      setStatus({
        type: "success",
        msg:
          resp?.message ||
          "VM creation started. Use the refresh icon below to check status.",
      });
    } catch (err) {
      setStatus({
        type: "danger",
        msg:
          err?.response?.data?.message ||
          err?.message ||
          "Failed to create VM.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------- Refresh Status (list from tc_user_vms) --------------------- */
  const handleRefreshStatus = async () => {
    setStatusError("");
    setVmRows([]);

    try {
      setStatusLoading(true);

      // Backend returns: { items: [ { org_name, user_name, instance_id, instance_name, launchTime, max_hours, created_by, state? } ] }
      const resp = await listUserVmsAPI();
      const items = Array.isArray(resp?.items) ? resp.items : [];

      setVmRows(items);

      if (!items.length) {
        setStatusError(
          resp?.message ||
            "No VM records found. Create a VM first."
        );
      }
    } catch (err) {
      setStatusError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch VM records."
      );
    } finally {
      setStatusLoading(false);
    }
  };

  const handleStartVm = async (row) => {
    if (!row.instance_id) {
      setStatusError("Instance ID is missing for this VM row.");
      return;
    }
    try {
      setStatusError("");
      setStatusLoading(true);

      await startUserVmInstanceAPI(row.instance_id);

      // Optimistically mark as "pending" and re-fetch statuses
      setVmRows((prev) =>
        prev.map((r) =>
          r.instance_id === row.instance_id ? { ...r, state: "pending" } : r
        )
      );

      await handleRefreshStatus();
    } catch (err) {
      console.error("Failed to start VM:", err);
      setStatusError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to start VM instance."
      );
    } finally {
      setStatusLoading(false);
    }
  };

  const handleStopVm = async (row) => {
    if (!row.instance_id) {
      setStatusError("Instance ID is missing for this VM row.");
      return;
    }
    try {
      setStatusError("");
      setStatusLoading(true);

      await stopUserVmInstanceAPI(row.instance_id);

      // Optimistically mark as "stopping" and re-fetch statuses
      setVmRows((prev) =>
        prev.map((r) =>
          r.instance_id === row.instance_id ? { ...r, state: "stopping" } : r
        )
      );

      await handleRefreshStatus();
    } catch (err) {
      console.error("Failed to stop VM:", err);
      setStatusError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to stop VM instance."
      );
    } finally {
      setStatusLoading(false);
    }
  };



  return (
    <div className="page-wrapper overflow-hidden">
      {/* ---------- CREATE VM CARD ---------- */}
      <Row className="justify-content-center muc-container">
        <Col sm="12" md="12" lg="12">
          <ComponentCard>
            <h4 className="muc-title">Create New User VM</h4>

            {status.msg && (
              <Alert
                color={status.type}
                isOpen={!!status.msg}
                toggle={() => setStatus({ type: "", msg: "" })}
              >
                {status.msg}
              </Alert>
            )}

            <div style={{ display: "flex", justifyContent: "center" }}>
              <Card className="muc-card" style={{ maxWidth: "600px", width: "100%" }}>
                <CardBody className="muc-card-body">
                  <Form onSubmit={handleSubmit}>
                    {/* Organization */}
                    <div className="muc-field-row">
                      <div className="muc-label-cell">Organization:</div>
                      <div>
                        <Input
                          type="select"
                          className="muc-input"
                          value={targetOrg}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTargetOrg(v);
                            setTargetUser("");  // reset user on org change
                          }}
                          required
                        >
                          <option value="" disabled>
                            {orgLoading ? "Loading organizations..." : "Select organization"}
                          </option>
                          {orgOptions.map((o) => (
                            <option key={o} value={o}>
                              {o}
                            </option>
                          ))}
                        </Input>
                      </div>
                    </div>

                    {/* Username */}
                    <div className="muc-field-row">
                      <div className="muc-label-cell">Assign to User:</div>
                      <div>
                        <Input
                          type="select"
                          className="muc-input"
                          value={targetUser}
                          onChange={(e) => setTargetUser(e.target.value)}
                          disabled={!targetOrg || usersLoading}
                          required
                        >
                          <option value="" disabled>
                            {!targetOrg
                              ? "Select organization first"
                              : usersLoading
                              ? "Loading users..."
                              : "Select user"}
                          </option>
                          {userOptions.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </Input>
                      </div>
                    </div>

                    {/* Instance Name */}
                    <div className="muc-field-row">
                      <div className="muc-label-cell">Instance Name:</div>
                      <div>
                        <Input
                          className="muc-input"
                          value={instanceName}
                          onChange={(e) => setInstanceName(e.target.value)}
                          placeholder="e.g. training-gpu-01"
                        />
                      </div>
                    </div>

                    {/* Credits / Cost acknowledgement */}
                    <div className="muc-field-row">
                      <div className="muc-label-cell">Credits / Cost:</div>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <Input
                          type="checkbox"
                          checked={acceptCosts}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setAcceptCosts(checked);
                            if (acceptCostsErr) {
                              setAcceptCostsErr(
                                validateAcceptCosts(checked)
                              );
                            }
                          }}
                          onBlur={() =>
                            setAcceptCostsErr(
                              validateAcceptCosts(acceptCosts)
                            )
                          }
                          style={{ width: "20px", height: "18px" }}
                        />
                        <span
                          style={{
                            marginLeft: 10,
                            fontSize: "12px",
                            color: "#666",
                          }}
                        >
                          I understand this will start a cloud VM and consume
                          credits / incur cloud charges while it is running.
                        </span>
                      </div>
                      {acceptCostsErr && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "12px",
                            marginTop: 4,
                          }}
                        >
                          {acceptCostsErr}
                        </div>
                      )}
                    </div>

                    <div className="muc-submit-bar">
                      <Button
                        className="submit-action-button muc-submit-btn"
                        disabled={submitting || !formValid}
                        type="submit"
                      >
                        {submitting ? (
                          <>
                            <Spinner size="sm" style={{ marginRight: 8 }} />
                            Creating...
                          </>
                        ) : (
                          "Create VM"
                        )}
                      </Button>
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </div>
          </ComponentCard>
        </Col>
      </Row>

      {/* ---------- STATUS CARD ---------- */}
      <Row className="justify-content-center muc-container">
        <Col sm="12" md="12" lg="12" style={{ marginTop: 40 }}>
          <ComponentCard>
            <h4 className="muc-title">User VM Mappings</h4>

            {statusError && (
              <Alert
                color="danger"
                isOpen={!!statusError}
                toggle={() => setStatusError("")}
              >
                {statusError}
              </Alert>
            )}

            <div className="muc-wrap" style={{ marginTop: 24 }}>
              <Card className="muc-card">
                <CardBody className="muc-card-body">
                  {/* Refresh row with icon-only button */}
                  <div
                    className="muc-field-row"
                    style={{ marginBottom: 24, alignItems: "center" }}
                  >
                    
                    <div
                      style={{
                        marginLeft: "auto", 
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <Button
                        className="submit-action-button muc-submit-btn"
                        type="button"
                        disabled={statusLoading}
                        onClick={handleRefreshStatus}
                        title="Refresh VM mappings"
                        style={{
                          padding: "6px 10px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {statusLoading ? (
                          <Spinner size="sm" />
                        ) : (
                          <RefreshIcon fontSize="small" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Results table */}
                  <div style={{ marginTop: 16, overflowX: "auto", width: "100%" }}>
                    <Table
                      bordered
                      hover
                      size="md"
                      style={{
                        width: "100%",
                        minWidth: "900px",
                        tableLayout: "auto",
                      }}
                    >
                      <thead>
                        <tr>
                          <th style={{ whiteSpace: "nowrap" }}>Organization</th>
                          <th style={{ whiteSpace: "nowrap" }}>User</th>
                          <th style={{ whiteSpace: "nowrap" }}>Instance Name</th>
                          <th style={{ whiteSpace: "nowrap" }}>Instance ID</th>
                          <th style={{ whiteSpace: "nowrap" }}>Launch Time (UTC)</th>
                          <th style={{ whiteSpace: "nowrap" }}>Max Hours</th>
                          <th style={{ whiteSpace: "nowrap" }}>Created By</th>
                          <th style={{ whiteSpace: "nowrap" }}>Status</th>
                          <th style={{ whiteSpace: "nowrap" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statusLoading && vmRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={9}
                              style={{ textAlign: "center", color: "#777" }}
                            >
                              Loading...
                            </td>
                          </tr>
                        ) : vmRows.length === 0 ? (
                          <tr>
                            <td
                              colSpan={9}
                              style={{ textAlign: "center", color: "#777" }}
                            >
                              Click the refresh icon to load VM mappings.
                            </td>
                          </tr>
                        ) : (
                          vmRows.map((r, idx) => {
                            const rawState = r.state ?? "unknown";
                            const state =
                              typeof rawState === "string" ? rawState.toLowerCase() : "unknown";

                            const canStart = state === "stopped";
                            const canStop = state === "running";

                            return (
                              <tr key={`${r.org_name}#${r.user_name}#${idx}`}>
                                <td>{r.org_name}</td>
                                <td>{r.user_name}</td>
                                <td>{r.instance_name || "—"}</td>
                                <td>{r.instance_id || "—"}</td>
                                <td>{formatUtc(r.launchTime)}</td>
                                <td>
                                  {r.max_hours === "" || r.max_hours == null ? "—" : r.max_hours}
                                </td>
                                <td>{r.created_by || "—"}</td>

                                {/* 🔹 Status column */}
                                <td style={{ textTransform: "capitalize" }}>
                                  {state}
                                </td>

                                {/* Action column: show ONLY applicable button */}
                                <td>
                                  {canStart && (
                                    <Button
                                      color="link"
                                      size="sm"
                                      disabled={statusLoading}
                                      onClick={() => handleStartVm(r)}
                                      title="Start VM"
                                      style={{ padding: 0 }}
                                    >
                                      <PlayArrowIcon fontSize="small" style={{ color: "green" }} />
                                    </Button>
                                  )}

                                  {canStop && (
                                    <Button
                                      color="link"
                                      size="sm"
                                      disabled={statusLoading}
                                      onClick={() => handleStopVm(r)}
                                      title="Stop VM"
                                      style={{ padding: 0 }}
                                    >
                                      <StopIcon fontSize="small" style={{ color: "red" }} />
                                    </Button>
                                  )}
                                </td>

                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </Table>
                  </div>
                </CardBody>
              </Card>
            </div>
          </ComponentCard>
        </Col>
      </Row>
    </div>
  );
}
